<template>
  <div class="notification-center">
    <UiMenu ref="menuRef" align="right">
      <template #trigger>
        <button
          type="button"
          class="notification-center__trigger"
          :class="{ 'notification-center__trigger--pulse': hasNewNotifications }"
          aria-label="Уведомления"
        >
          <span class="notification-center__bell">🔔</span>
          <UiBadge v-if="unreadCount > 0" variant="accent" class="notification-center__badge">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </UiBadge>
        </button>
      </template>

      <div class="notification-center__panel">
        <header class="notification-center__header">
          <span class="notification-center__title">Уведомления</span>
          <div class="notification-center__header-actions">
            <UiButton
              v-if="unreadCount > 0"
              variant="secondary"
              class="notification-center__btn-text"
              @click="markAllAsRead"
            >
              Прочитать все
            </UiButton>
            <UiMenu align="right">
              <template #trigger>
                <button type="button" class="notification-center__dots" aria-label="Ещё">⋮</button>
              </template>
              <button type="button" class="notification-center__menu-item" @click="clearRead">
                Очистить прочитанные
              </button>
              <button type="button" class="notification-center__menu-item" @click="clearAll">
                Очистить все
              </button>
              <button
                type="button"
                class="notification-center__menu-item notification-center__menu-item--divider"
                @click="requestPermission"
              >
                Настроить уведомления
              </button>
            </UiMenu>
          </div>
        </header>

        <div v-if="!isConnected" class="notification-center__alert-wrap">
          <UiAlert type="warning">
            <div class="notification-center__alert-inner">
              <span>{{ connectionStatusText }}</span>
              <UiButton
                variant="secondary"
                class="notification-center__btn-text"
                @click="manualReconnect"
              >
                Переподключить
              </UiButton>
            </div>
          </UiAlert>
        </div>

        <div class="notification-center__tabs">
          <button
            type="button"
            class="notification-center__tab"
            :class="{ 'notification-center__tab--active': activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >
            Все
            <UiBadge v-if="notifications.length > 0" variant="neutral">{{
              notifications.length
            }}</UiBadge>
          </button>
          <button
            type="button"
            class="notification-center__tab"
            :class="{ 'notification-center__tab--active': activeFilter === 'unread' }"
            @click="activeFilter = 'unread'"
          >
            Непрочитанные
            <UiBadge v-if="unreadCount > 0" variant="accent">{{ unreadCount }}</UiBadge>
          </button>
        </div>

        <div class="notification-center__list">
          <template v-if="filteredNotifications.length > 0">
            <div
              v-for="item in filteredNotifications"
              :key="item.id"
              :class="[
                'notification-center__item',
                { 'notification-center__item--unread': !item.read },
              ]"
              @click="handleNotificationClick(item)"
            >
              <span class="notification-center__item-icon">{{
                getNotificationIcon(item.type)
              }}</span>
              <div class="notification-center__item-body">
                <div class="notification-center__item-row">
                  <div class="notification-center__item-content">
                    <div class="notification-center__item-title">{{ item.title }}</div>
                    <div class="notification-center__item-message">{{ item.message }}</div>
                  </div>
                  <div class="notification-center__item-meta">
                    <span class="notification-center__item-time">{{
                      formatTime(item.createdAt)
                    }}</span>
                    <UiMenu align="right" @click.stop>
                      <template #trigger>
                        <button
                          type="button"
                          class="notification-center__dots notification-center__dots--sm"
                          aria-label="Действия"
                          @click.stop
                        >
                          ⋮
                        </button>
                      </template>
                      <button
                        v-if="!item.read"
                        type="button"
                        class="notification-center__menu-item"
                        @click.stop="markAsRead(item.id)"
                      >
                        Отметить как прочитанное
                      </button>
                      <button
                        v-if="item.actionUrl"
                        type="button"
                        class="notification-center__menu-item"
                        @click.stop="navigateToAction(item)"
                      >
                        {{ item.actionText || 'Перейти' }}
                      </button>
                      <button
                        type="button"
                        class="notification-center__menu-item"
                        @click.stop="removeNotification(item.id)"
                      >
                        Удалить
                      </button>
                    </UiMenu>
                  </div>
                </div>
                <UiButton
                  v-if="item.actionUrl"
                  variant="secondary"
                  class="notification-center__item-action"
                  @click.stop="navigateToAction(item)"
                >
                  {{ item.actionText || 'Перейти' }}
                </UiButton>
              </div>
              <div v-if="!item.read" class="notification-center__item-indicator" />
            </div>
          </template>
          <div v-else class="notification-center__empty">
            <span class="notification-center__empty-icon">{{
              activeFilter === 'unread' ? '✓' : '🔔'
            }}</span>
            <div class="notification-center__empty-title">
              {{ activeFilter === 'unread' ? 'Нет непрочитанных уведомлений' : 'Нет уведомлений' }}
            </div>
            <div class="notification-center__empty-text">
              {{
                activeFilter === 'unread'
                  ? 'Все уведомления прочитаны'
                  : 'Уведомления будут появляться здесь'
              }}
            </div>
          </div>
        </div>

        <footer class="notification-center__footer">
          <UiButton variant="secondary" class="notification-center__btn-text" @click="closeMenu">
            Закрыть
          </UiButton>
        </footer>
      </div>
    </UiMenu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import UiAlert from '@/shared/ui/UiAlert.vue'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiMenu from '@/shared/ui/UiMenu.vue'
import { useRealTimeNotifications } from '@/modules/common/public'
import { useMercureConnection } from '@/modules/common/public'
import type { Notification, NotificationType } from '../services/websocket/types'

const router = useRouter()
const menuRef = ref<{ close: () => void } | null>(null)

const {
  notifications,
  unreadCount,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearAll,
  clearRead,
  requestPermission,
  loadFromServer,
} = useRealTimeNotifications()

onMounted(() => {
  void loadFromServer()
})

const { isConnected, connectionStatusText, manualReconnect } = useMercureConnection()

const activeFilter = ref<'all' | 'unread'>('all')
const hasNewNotifications = ref(false)

const filteredNotifications = computed(() => {
  if (activeFilter.value === 'unread') return notifications.value.filter((n) => !n.read)
  return notifications.value
})

watch(unreadCount, (newCount, oldCount) => {
  if (newCount > oldCount) {
    hasNewNotifications.value = true
    setTimeout(() => {
      hasNewNotifications.value = false
    }, 2000)
  }
})

function closeMenu() {
  menuRef.value?.close()
}

function handleNotificationClick(notification: Notification) {
  if (!notification.read) markAsRead(notification.id)
  if (notification.actionUrl) navigateToAction(notification)
}

function navigateToAction(notification: Notification) {
  if (!notification.actionUrl) return
  if (notification.actionUrl.startsWith('http')) {
    window.open(notification.actionUrl, '_blank')
  } else {
    router.push(notification.actionUrl)
    closeMenu()
  }
}

function getNotificationIcon(type: NotificationType): string {
  const icons: Record<string, string> = {
    info: 'ℹ',
    success: '✓',
    warning: '⚠',
    error: '!',
    system: '⚙',
  }
  return icons[type] ?? 'ℹ'
}

function formatTime(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Только что'
  if (minutes < 60) return `${minutes} мин назад`
  if (minutes < 1440) return `${Math.floor(minutes / 60)} ч назад`
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<style scoped>
.notification-center {
  position: relative;
}

.notification-center__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.notification-center__trigger:hover {
  background: #f1f5f9;
}

.notification-center__trigger--pulse {
  animation: notification-pulse 2s infinite;
}

@keyframes notification-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.notification-center__bell {
  display: block;
}

.notification-center__badge {
  position: absolute;
  top: 2px;
  right: 2px;
}

.notification-center__panel {
  width: 400px;
  max-width: calc(100vw - 32px);
  max-height: 500px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.notification-center__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.notification-center__title {
  font-size: 16px;
  font-weight: 600;
}

.notification-center__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-center__btn-text {
  padding: 4px 8px;
  font-size: 13px;
}

.notification-center__dots {
  padding: 4px 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #586578;
  border-radius: 4px;
}

.notification-center__dots:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.notification-center__dots--sm {
  font-size: 14px;
}

.notification-center__menu-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  text-align: left;
  font: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.notification-center__menu-item:hover {
  background: #f1f5f9;
}

.notification-center__menu-item--divider {
  border-top: 1px solid #e2e8f0;
}

.notification-center__alert-wrap {
  padding: 12px 16px;
}

.notification-center__alert-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.notification-center__tabs {
  display: flex;
  gap: 4px;
  padding: 8px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.notification-center__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font: inherit;
  font-size: 14px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #586578;
}

.notification-center__tab--active {
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 500;
}

.notification-center__list {
  max-height: 300px;
  overflow-y: auto;
  flex: 1;
}

.notification-center__item {
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;
}

.notification-center__item:hover {
  background: #f8fafc;
}

.notification-center__item--unread {
  background: #f0f9ff;
}

.notification-center__item-icon {
  display: none;
}

.notification-center__item-body {
  min-width: 0;
}

.notification-center__item-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.notification-center__item-content {
  flex: 1;
  min-width: 0;
}

.notification-center__item-title {
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-center__item-message {
  font-size: 13px;
  color: #586578;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-center__item-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.notification-center__item-time {
  font-size: 12px;
  color: #586578;
}

.notification-center__item-action {
  margin-top: 8px;
}

.notification-center__item-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: #0f172a;
  border-radius: 0 2px 2px 0;
}

.notification-center__empty {
  text-align: center;
  padding: 48px 24px;
}

.notification-center__empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.5;
}

.notification-center__empty-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.notification-center__empty-text {
  font-size: 14px;
  color: #586578;
}

.notification-center__footer {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.notification-center__list::-webkit-scrollbar {
  width: 4px;
}

.notification-center__list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}
</style>
